import { fromZonedTime } from 'date-fns-tz';

import { TIMEZONE_NAME } from '@data/constants';

export const formatDateShort = (date: string): string =>
	new Date(date).toLocaleDateString('en-US', {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
		timeZone: TIMEZONE_NAME,
	});

export const formatDateLong = (date: string): string => {
	const parsedDate = new Date(date);
	const weekday = parsedDate.toLocaleDateString('en-US', {
		weekday: 'long',
		timeZone: TIMEZONE_NAME,
	});
	const day = parsedDate.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: TIMEZONE_NAME,
	});
	const month = parsedDate.toLocaleDateString('en-US', {
		month: 'long',
		timeZone: TIMEZONE_NAME,
	});
	const year = parsedDate.toLocaleDateString('en-US', {
		year: 'numeric',
		timeZone: TIMEZONE_NAME,
	});
	return `${weekday} ${month} ${day}, ${year}`;
};

export const formatTime = (date: string): string =>
	new Date(date).toLocaleTimeString('en-US', {
		timeZone: TIMEZONE_NAME,
		hour: 'numeric',
		minute: 'numeric',
	});

export const formatDateHuman = (dateString: string) => {
	const compareDate = formatDateShort(dateString);
	const today = new Date();
	const tomorrow = new Date();
	tomorrow.setDate(today.getDate() + 1);
	if (formatDateShort(today.toUTCString()) === compareDate) {
		return 'Today';
	}
	if (formatDateShort(tomorrow.toUTCString()) === compareDate) {
		return 'Tomorrow';
	}
	return compareDate;
};

export const getWeekStartAndEndDate = (offsetByNumberOfWeeks = 0) => {
	const startDate = new Date();
	const endDate = new Date();

	// Treat monday as the first day
	const startDay = startDate.getDate() - startDate.getDay() + 1;
	const endDay = startDay + 6;

	startDate.setDate(startDay);
	endDate.setDate(endDay);

	if (offsetByNumberOfWeeks > 0) {
		startDate.setDate(startDate.getDate() + offsetByNumberOfWeeks * 7);
		endDate.setDate(endDate.getDate() + offsetByNumberOfWeeks * 7);
	}

	return {
		startDate,
		endDate,
	};
};

const getStartAndEndDateFromToday = (offsetByNumberOfDays = 0) => {
	const startDate = new Date();
	const endDate = new Date();

	const startDay = startDate.getDate();
	const endDay = startDay + offsetByNumberOfDays;

	startDate.setDate(startDay);
	endDate.setDate(endDay);

	return {
		startDate,
		endDate,
	};
};

/** Convert dates to midnight for a time range */
const convertToMidnight = (startDate: Date, endDate: Date) => {
	// A day later in order to include the last day
	endDate.setDate(endDate.getDate() + 1);

	return [startDate, endDate].map((dateObject) => {
		const [dateString] = dateObject.toISOString().split('T');

		// Midnight for defined timezone in UTC
		const utcDate = fromZonedTime(`${dateString} 00:00:00`, TIMEZONE_NAME);

		return utcDate;
	});
};

export const getDaysInMonth = (year: number, month: number) => {
	return new Date(year, month, 0).getDate();
};

export const getMonthStartAndEndDate = (offsetByNumberOfMonth = 0) => {
	const startDate = new Date();
	const endDate = new Date();

	if (offsetByNumberOfMonth > 0) {
		startDate.setMonth(startDate.getMonth() + offsetByNumberOfMonth);
		endDate.setMonth(endDate.getMonth() + offsetByNumberOfMonth);
	}

	const endDay = getDaysInMonth(
		startDate.getFullYear(),
		startDate.getMonth() + 1,
	);

	startDate.setDate(1);
	endDate.setDate(endDay);

	return {
		startDate,
		endDate,
	};
};

/** Get week date range for the current week */
export const getWeekDateRange = (offsetByNumberOfWeeks = 0) => {
	const { startDate, endDate } = getWeekStartAndEndDate(offsetByNumberOfWeeks);
	return convertToMidnight(startDate, endDate);
};

/** Get week date range from today + the next 7 days */
export const getWeekDateRangeFromToday = () => {
	const { startDate, endDate } = getStartAndEndDateFromToday(7);
	return convertToMidnight(startDate, endDate);
};

/** Get month date range for the current month */
export const getMonthDateRange = (offsetByNumberOfMonths = 0) => {
	const { startDate, endDate } = getMonthStartAndEndDate(
		offsetByNumberOfMonths,
	);
	return convertToMidnight(startDate, endDate);
};

/** Get month date range from today + the next 30 days */
export const getMonthDateRangeFromToday = () => {
	const { startDate, endDate } = getStartAndEndDateFromToday(30);
	return convertToMidnight(startDate, endDate);
};

export const getTimeSince = (date: string) => {
	const dateObject = new Date(date);
	const now = Date.now();

	const millisecondsSince = now - dateObject.getTime();

	const hours = millisecondsSince / (60 * 60 * 1000);
	const minutes = millisecondsSince / (60 * 1000);

	return {
		hours,
		minutes,
	};
};
